import React, { useState } from "react";
import { supabase } from "./supabaseClient";
import { Browser } from "@capacitor/browser";

const ELDE_BLUE = "#4F7FFA";
const ELDE_CYAN = "#00BCD4";
const ELDE_DARK = "#1a1a2e";
const ELDE_DARKER = "#121220";

export default function Auth({ onAuth, onBack, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // "login" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const clearMessages = () => { setError(""); setMessage(""); };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearMessages();
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        setMessage("Check your email for a confirmation link!");
      } else if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // onAuth will be triggered by the auth state listener in App.js
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        setMessage("Password reset link sent to your email!");
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Use deep link for native app, web URL for browser
  const getRedirectUrl = () => {
    const isNative = window.location.href.startsWith("https://localhost") ||
      window.Capacitor?.isNativePlatform?.();
    return isNative ? "app.elde.mobile://" : window.location.origin;
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    clearMessages();
    try {
      const isNative = window.Capacitor?.isNativePlatform?.();
      if (isNative) {
        // Use in-app browser (SFSafariViewController) to satisfy Apple Guideline 4
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: { redirectTo: getRedirectUrl(), skipBrowserRedirect: true },
        });
        if (error) throw error;
        await Browser.open({ url: data.url });
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: { redirectTo: getRedirectUrl() },
        });
        if (error) throw error;
      }
      setTimeout(() => setLoading(false), 3000);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAppleAuth = async () => {
    setLoading(true);
    clearMessages();
    try {
      const isNative = window.Capacitor?.isNativePlatform?.();
      if (isNative) {
        // Use in-app browser (SFSafariViewController) to satisfy Apple Guideline 4
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: "apple",
          options: { redirectTo: getRedirectUrl(), skipBrowserRedirect: true },
        });
        if (error) throw error;
        await Browser.open({ url: data.url });
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: "apple",
          options: { redirectTo: getRedirectUrl() },
        });
        if (error) throw error;
      }
      setTimeout(() => setLoading(false), 3000);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Background grid */}
      <div style={styles.bgGrid} />

      {/* Glow accents */}
      <div style={styles.glowBlue} />
      <div style={styles.glowCyan} />

      <div style={styles.card}>
        {onBack && (
          <button onClick={onBack} style={{
            background: "none", border: "none", color: "#8899BB",
            fontSize: 13, cursor: "pointer", padding: "0 0 20px 0",
            display: "flex", alignItems: "center", gap: 6, fontWeight: 600,
          }}>
            ← Back
          </button>
        )}
        {/* Logo */}
        <div style={styles.logoWrap}>
          <div style={styles.logo}>
            <span style={styles.logoEl}>EL</span>
            <span style={styles.logoDe}>DE</span>
          </div>

        </div>

        {/* Tab switcher */}
        {mode !== "forgot" && (
          <div style={styles.tabs}>
            <button
              style={{ ...styles.tab, ...(mode === "login" ? styles.tabActive : {}) }}
              onClick={() => { setMode("login"); clearMessages(); }}
            >
              Sign In
            </button>
            <button
              style={{ ...styles.tab, ...(mode === "signup" ? styles.tabActive : {}) }}
              onClick={() => { setMode("signup"); clearMessages(); }}
            >
              Create Account
            </button>
          </div>
        )}

        {mode === "forgot" && (
          <div style={styles.forgotHeader}>
            <button style={styles.backBtn} onClick={() => { setMode("login"); clearMessages(); }}>
              ← Back to Sign In
            </button>
            <div style={styles.forgotTitle}>Reset Password</div>
            <div style={styles.forgotSub}>Enter your email and we'll send you a reset link.</div>
          </div>
        )}

        {/* Social auth buttons */}
        {mode !== "forgot" && (
          <div style={styles.socialRow}>
            <button style={styles.socialBtn} onClick={handleGoogleAuth} disabled={loading}>
              <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
            <button style={styles.socialBtn} onClick={handleAppleAuth} disabled={loading}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Continue with Apple
            </button>
          </div>
        )}

        {mode !== "forgot" && (
          <div style={styles.divider}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerText}>or</span>
            <div style={styles.dividerLine} />
          </div>
        )}

        {/* Email form */}
        <form onSubmit={handleEmailAuth} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={styles.input}
            />
          </div>

          {mode !== "forgot" && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                style={styles.input}
              />
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}
          {message && <div style={styles.success}>{message}</div>}

          <button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? "Please wait..." : mode === "login" ? "Sign In" : mode === "signup" ? "Create Account" : "Send Reset Link"}
          </button>
        </form>

        {/* Forgot password link */}
        {mode === "login" && (
          <button style={styles.forgotLink} onClick={() => { setMode("forgot"); clearMessages(); }}>
            Forgot password?
          </button>
        )}

        {/* Terms */}
        {mode === "signup" && (
          <div style={styles.terms}>
            By creating an account you agree to our{" "}
            <a href="https://elde.ai/privacy.html" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Privacy Policy</a>{" "}
            and{" "}
            <a href="https://elde.ai/terms.html" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Terms of Service</a>.
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: ELDE_DARKER,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  bgGrid: {
    position: "absolute", inset: 0,
    backgroundImage: `linear-gradient(${ELDE_DARK}44 1px, transparent 1px), linear-gradient(90deg, ${ELDE_DARK}44 1px, transparent 1px)`,
    backgroundSize: "40px 40px",
    opacity: 0.6,
  },
  glowBlue: {
    position: "absolute", top: -100, right: -100,
    width: 400, height: 400, borderRadius: "50%",
    background: `radial-gradient(circle, ${ELDE_BLUE}33 0%, transparent 70%)`,
    pointerEvents: "none",
  },
  glowCyan: {
    position: "absolute", bottom: -100, left: -100,
    width: 350, height: 350, borderRadius: "50%",
    background: `radial-gradient(circle, ${ELDE_CYAN}22 0%, transparent 70%)`,
    pointerEvents: "none",
  },
  card: {
    background: ELDE_DARK,
    border: `1px solid rgba(255,255,255,0.08)`,
    borderRadius: 20,
    padding: "36px 32px",
    width: "100%",
    maxWidth: 420,
    position: "relative",
    zIndex: 1,
    boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
  },
  logoWrap: {
    textAlign: "center",
    marginBottom: 28,
  },
  logo: {
    display: "inline-flex",
    border: `2px solid ${ELDE_CYAN}`,
    borderRadius: 8,
    padding: "4px 12px",
    marginBottom: 8,
  },
  logoEl: {
    fontSize: 26, fontWeight: 900, color: "#fff", letterSpacing: 2,
  },
  logoDe: {
    fontSize: 26, fontWeight: 900, color: ELDE_CYAN, letterSpacing: 2,
  },
  logoTagline: {
    fontSize: 13, color: "#8899BB", letterSpacing: 0.5,
  },
  tabs: {
    display: "flex",
    background: "rgba(255,255,255,0.05)",
    borderRadius: 10,
    padding: 4,
    marginBottom: 24,
    gap: 4,
  },
  tab: {
    flex: 1, padding: "9px 0", borderRadius: 8, border: "none",
    background: "transparent", color: "#8899BB",
    fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
  },
  tabActive: {
    background: ELDE_BLUE, color: "#fff",
    boxShadow: `0 2px 12px ${ELDE_BLUE}66`,
  },
  forgotHeader: {
    marginBottom: 24,
  },
  backBtn: {
    background: "none", border: "none", color: "#8899BB",
    fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 12,
  },
  forgotTitle: {
    fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 6,
  },
  forgotSub: {
    fontSize: 13, color: "#8899BB",
  },
  socialRow: {
    display: "flex", flexDirection: "column", gap: 10, marginBottom: 20,
  },
  socialBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10, padding: "11px 16px", color: "#fff",
    fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.2s",
  },
  divider: {
    display: "flex", alignItems: "center", gap: 12, marginBottom: 20,
  },
  dividerLine: {
    flex: 1, height: 1, background: "rgba(255,255,255,0.08)",
  },
  dividerText: {
    fontSize: 12, color: "#8899BB", fontWeight: 500,
  },
  form: {
    display: "flex", flexDirection: "column", gap: 16,
  },
  fieldGroup: {
    display: "flex", flexDirection: "column", gap: 6,
  },
  label: {
    fontSize: 12, fontWeight: 600, color: "#8899BB", textTransform: "uppercase", letterSpacing: 0.5,
  },
  input: {
    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10, padding: "12px 14px", color: "#fff", fontSize: 15,
    outline: "none", transition: "border 0.2s",
  },
  error: {
    background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
    borderRadius: 8, padding: "10px 14px", color: "#FCA5A5", fontSize: 13,
  },
  success: {
    background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)",
    borderRadius: 8, padding: "10px 14px", color: "#6EE7B7", fontSize: 13,
  },
  submitBtn: {
    background: `linear-gradient(135deg, ${ELDE_BLUE}, #3B6FE8)`,
    border: "none", borderRadius: 10, padding: "13px 0",
    color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer",
    boxShadow: `0 4px 20px ${ELDE_BLUE}55`, marginTop: 4,
    transition: "opacity 0.2s",
  },
  forgotLink: {
    background: "none", border: "none", color: "#8899BB",
    fontSize: 13, cursor: "pointer", textAlign: "center",
    display: "block", width: "100%", marginTop: 14, padding: 0,
  },
  terms: {
    fontSize: 11, color: "#8899BB", textAlign: "center", marginTop: 16, lineHeight: 1.6,
  },
  termsLink: {
    color: ELDE_BLUE, textDecoration: "none",
  },
};
